import React, { useState, useEffect } from "react";
import {
  DescriptionDetails,
  DescriptionList,
  DescriptionTerm,
} from "../../design/description-list";
import { Heading, Subheading } from "../../design/heading";
import { ChevronLeftIcon } from "@heroicons/react/16/solid";
import {
  ExclamationTriangleIcon,
  SparklesIcon,
  CheckCircleIcon,
  PencilSquareIcon,
} from "@heroicons/react/24/outline";
import { Link } from "../../design/link";
import { Divider } from "../../design/divider";
import LoadingView from "../../components/LoadingView";
import ReportStatusBadge from "../../components/ReportStatusBadge";
import moment from "moment";
import DownloadFileButton from "../../components/DownloadFileButton";
import { Text } from "../../design/text";
import CreateFL324PDialog from "../../components/Dialogs/CreateFL324PDialog";
import { Button } from "../../design/button";
import DeleteReportButton from "./components/DeleteReportButton";
import RegenerateReportPDFButton from "./components/RegenerateReportPDFButton";
import ViewEventsDialog from "./components/ViewEventsDialog";
import ErrorView from "../../components/ErrorView";
import { useReportDetail } from "./hooks/useReportDetail";
import { useReport } from "../../contexts/ReportContext";
import { useAuth } from "../../contexts/AuthContext";
import { useAccount } from "../../contexts/AccountContext";
import { ClipboardDocumentListIcon } from "@heroicons/react/24/outline";
import { Textarea } from "../../design/textarea";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../../design/dialog";

// Breadcrumb Navigation
const Breadcrumb: React.FC = () => (
  <div className="max-lg:hidden">
    <Link
      href="/reports"
      className="inline-flex items-center gap-2 transition-colors text-sm/6 text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-300"
    >
      <ChevronLeftIcon className="size-4 fill-zinc-400 dark:fill-zinc-500" />
      Reports
    </Link>
  </div>
);

// Pending Report Banner
interface PendingBannerProps {
  isAIGenerated?: boolean;
  hasBeenEdited?: boolean;
}

const PendingReportBanner: React.FC<PendingBannerProps> = ({
  isAIGenerated,
  hasBeenEdited,
}) => {
  if (isAIGenerated && !hasBeenEdited) {
    return (
      <div className="p-4 border rounded-xl bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800">
        <div className="flex items-start gap-3">
          <SparklesIcon className="w-5 h-5 mt-0.5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-medium text-amber-800 dark:text-amber-200">
              AI-Generated Report - Review Required
            </h3>
            <p className="mt-1 text-sm text-amber-700 dark:text-amber-300">
              This report was automatically generated and may contain inaccuracies.
              Please review the content carefully, make any necessary edits, and save your changes before approving.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 border rounded-xl bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
      <div className="flex items-start gap-3">
        <PencilSquareIcon className="w-5 h-5 mt-0.5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
        <div>
          <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200">
            Report Pending Review
          </h3>
          <p className="mt-1 text-sm text-blue-700 dark:text-blue-300">
            Please review and edit the report content below. Once you're satisfied with the report, save your changes and then approve it.
          </p>
        </div>
      </div>
    </div>
  );
};

// Approve Dialog
interface ApproveDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isApproving: boolean;
}

const ApproveDialog: React.FC<ApproveDialogProps> = ({
  open,
  onClose,
  onConfirm,
  isApproving,
}) => (
  <Dialog open={open} onClose={onClose}>
    <DialogTitle>Approve Report</DialogTitle>
    <DialogBody>
      <div className="flex items-start gap-3">
        <ExclamationTriangleIcon className="w-6 h-6 text-amber-500 flex-shrink-0" />
        <div>
          <p className="text-sm text-zinc-700 dark:text-zinc-300">
            Are you sure you want to approve this report? Once approved, a PDF will be generated and the report will be locked for legal compliance.
          </p>
          <p className="mt-2 text-sm font-medium text-zinc-900 dark:text-zinc-100">
            This action cannot be undone.
          </p>
        </div>
      </div>
    </DialogBody>
    <DialogActions>
      <Button plain onClick={onClose} disabled={isApproving}>
        Cancel
      </Button>
      <Button color="green" onClick={onConfirm} disabled={isApproving}>
        {isApproving ? "Approving..." : "Approve Report"}
      </Button>
    </DialogActions>
  </Dialog>
);

const ReportDetailPage: React.FC = () => {
  const { isAdmin } = useAuth();
  const { account } = useAccount();
  const showStaffInitialsOnly = account?.reportSettings?.showStaffInitialsOnly ?? false;
  const { updateReport } = useReport();
  const {
    selectedReport,
    loading,
    error,
    supervisor,
    selectedCase,
    isFL324PExportEnabled,
    reportId,
    createFL324PDialogOpen,
    setCreateFL324PDialogOpen,
  } = useReportDetail();

  const [viewEventsDialogOpen, setViewEventsDialogOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedReport, setEditedReport] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Initialize edited report when report loads
  useEffect(() => {
    if (selectedReport?.report !== undefined) {
      setEditedReport(selectedReport.report || "");
    }
  }, [selectedReport?.report]);

  // Track unsaved changes
  useEffect(() => {
    if (selectedReport) {
      setHasUnsavedChanges(editedReport !== (selectedReport.report || ""));
    }
  }, [editedReport, selectedReport]);

  // Auto-enable editing for pending reports
  useEffect(() => {
    if (selectedReport?.status === "pending") {
      setIsEditing(true);
    }
  }, [selectedReport?.status]);

  const handleSave = async () => {
    if (!selectedReport) return;

    setIsSaving(true);
    try {
      await updateReport(selectedReport.id, {
        report: editedReport,
      });
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error("Failed to save report:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedReport) return;

    setIsApproving(true);
    try {
      await updateReport(selectedReport.id, {
        status: "approved",
      });
      setApproveDialogOpen(false);
    } catch (error) {
      console.error("Failed to approve report:", error);
    } finally {
      setIsApproving(false);
    }
  };

  // Early returns for loading and error states
  if (loading.details) {
    return <LoadingView />;
  }

  if (error.details) {
    return <ErrorView error={error.details} />;
  }

  if (!selectedReport) {
    return <ErrorView error={new Error("Report not found")} />;
  }

  const isPending = selectedReport.status === "pending";
  // Approved/signed reports are locked for legal compliance — editing is not
  // allowed once approved (the approve dialog warns this is permanent).
  const isLocked = ["approved", "signed"].includes(selectedReport.status);
  const canGeneratePDF = ["approved", "completed"].includes(selectedReport.status);
  const hasBeenEdited = !!selectedReport.updatedBy;

  return (
    <div className="space-y-6">
      {/* FL-324P Dialog */}
      {isFL324PExportEnabled && selectedCase && (
        <CreateFL324PDialog
          caseItem={selectedCase}
          reportId={reportId}
          isOpen={createFL324PDialogOpen}
          setIsOpen={setCreateFL324PDialogOpen}
        />
      )}

      {/* View Events Dialog */}
      {selectedReport.visitId && (
        <ViewEventsDialog
          visitId={selectedReport.visitId}
          accountId={selectedReport.accountId}
          isOpen={viewEventsDialogOpen}
          onClose={() => setViewEventsDialogOpen(false)}
        />
      )}

      {/* Approve Dialog */}
      <ApproveDialog
        open={approveDialogOpen}
        onClose={() => setApproveDialogOpen(false)}
        onConfirm={handleApprove}
        isApproving={isApproving}
      />

      {/* Breadcrumb Navigation */}
      <Breadcrumb />

      {/* Report Header */}
      <div className="mt-4 lg:mt-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <Heading>
              Report for {moment(selectedReport.visit?.startDate.toDate()).format("LLL")}
            </Heading>
            <div className="flex items-center gap-2 mt-2">
              <ReportStatusBadge status={selectedReport.status} />
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* View Events button */}
            {selectedReport.visitId && (
              <Button outline onClick={() => setViewEventsDialogOpen(true)}>
                <ClipboardDocumentListIcon className="h-4 w-4 mr-2" />
                View Events
              </Button>
            )}
            {/* Download PDF button - only show if PDF exists */}
            {selectedReport.reportPDFPath && (
              <DownloadFileButton path={selectedReport.reportPDFPath} label="Download PDF" />
            )}
            {/* Generate/Regenerate PDF button - show for approved/completed reports */}
            {canGeneratePDF && (
              <RegenerateReportPDFButton
                reportId={selectedReport.id}
                hasExistingPDF={!!selectedReport.reportPDFPath}
                includeEvents={(selectedReport as any).includeEvents ?? false}
                hasVisit={!!selectedReport.visitId && !isLocked}
              />
            )}
            {isFL324PExportEnabled && (
              <Button outline onClick={() => setCreateFL324PDialogOpen(true)}>
                Create FL-324P
              </Button>
            )}
            {isAdmin && (
              <DeleteReportButton
                report={selectedReport}
                hasPDF={!!selectedReport.reportPDFPath}
                isLinkedToVisit={!!selectedReport.visitId}
              />
            )}
          </div>
        </div>
      </div>

      {/* Pending Report Banner */}
      {isPending && (
        <PendingReportBanner
          isAIGenerated={selectedReport.isAIGenerated}
          hasBeenEdited={hasBeenEdited}
        />
      )}

      {/* Report Info Card */}
      <div className="p-6 bg-white border rounded-xl shadow-sm dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700">
        <Subheading>Report Information</Subheading>
        <Divider className="my-4" />
        <DescriptionList>
          <DescriptionTerm>Visit</DescriptionTerm>
          <DescriptionDetails>
            <Link
              href={`/visit/${selectedReport.visitId}`}
              className="text-blue-600 dark:text-blue-400 hover:underline"
            >
              {moment(selectedReport.visit?.startDate.toDate()).format("MMMM Do, YYYY [at] h:mm A")}
            </Link>
          </DescriptionDetails>

          <DescriptionTerm>Monitor</DescriptionTerm>
          <DescriptionDetails>
            {supervisor ? (
              <Link
                href={`/supervisor/${supervisor.id}`}
                className="text-blue-600 dark:text-blue-400 hover:underline"
              >
                {showStaffInitialsOnly
                  ? [supervisor.firstName, supervisor.lastName]
                      .filter(Boolean)
                      .map(n => `${n![0].toUpperCase()}.`)
                      .join('')
                  : `${supervisor.firstName} ${supervisor.lastName}`}
              </Link>
            ) : (
              <span className="text-zinc-500 dark:text-zinc-400">Monitor not assigned</span>
            )}
          </DescriptionDetails>
        </DescriptionList>
      </div>

      {/* Report Content Card - Editable for pending reports */}
      <div className="p-6 bg-white border rounded-xl shadow-sm dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700">
        <div className="flex items-center justify-between mb-4">
          <Subheading>Report Content</Subheading>
          {!isPending && !isEditing && !isLocked && (
            <Button outline onClick={() => setIsEditing(true)}>
              <PencilSquareIcon className="h-4 w-4 mr-2" />
              Edit
            </Button>
          )}
          {isLocked && (
            <span className="text-sm text-zinc-500">Approved — locked</span>
          )}
        </div>
        <Divider className="my-4" />

        {isEditing ? (
          <div className="space-y-4">
            <Textarea
              value={editedReport}
              onChange={(e) => setEditedReport(e.target.value)}
              placeholder="Enter report content here..."
              rows={15}
              className="w-full font-normal"
            />
            <div className="flex items-center justify-between">
              <div className="text-sm text-zinc-500 dark:text-zinc-400">
                {hasUnsavedChanges && (
                  <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400">
                    <ExclamationTriangleIcon className="h-4 w-4" />
                    You have unsaved changes
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {!isPending && (
                  <Button
                    plain
                    onClick={() => {
                      setEditedReport(selectedReport.report || "");
                      setIsEditing(false);
                      setHasUnsavedChanges(false);
                    }}
                    disabled={isSaving}
                  >
                    Cancel
                  </Button>
                )}
                <Button
                  onClick={handleSave}
                  disabled={isSaving || !hasUnsavedChanges}
                >
                  {isSaving ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <>
            {selectedReport.report ? (
              <Text className="whitespace-pre-wrap leading-relaxed">
                {selectedReport.report}
              </Text>
            ) : (
              <p className="text-sm text-zinc-500 dark:text-zinc-400">
                No report content has been written yet.
              </p>
            )}
          </>
        )}
      </div>

      {/* Approve Action - Only show for pending reports */}
      {isPending && (
        <div className="sticky bottom-0 p-4 -mx-4 bg-white dark:bg-zinc-900 border-t border-zinc-200 dark:border-zinc-700 shadow-lg">
          <div className="flex items-center justify-between max-w-7xl mx-auto">
            <div className="flex items-center gap-2 text-sm text-zinc-600 dark:text-zinc-400">
              {hasUnsavedChanges ? (
                <>
                  <ExclamationTriangleIcon className="h-5 w-5 text-amber-500" />
                  <span>Save your changes before approving the report</span>
                </>
              ) : (
                <>
                  <CheckCircleIcon className="h-5 w-5 text-green-500" />
                  <span>Ready to approve this report?</span>
                </>
              )}
            </div>
            <Button
              color="green"
              onClick={() => setApproveDialogOpen(true)}
              disabled={hasUnsavedChanges || isSaving}
            >
              <CheckCircleIcon className="h-4 w-4 mr-2" />
              Approve Report
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportDetailPage;
